import { useState } from "react";
import { api } from "../api";
import { Btn, Card, Field, Input, Select, Textarea, Spinner } from "../components/Shared";
import { ROLE_LABELS, ROLE_ORDER } from "../auth/roles";

// Telling people something out of band — a downtime notice, typically. The one
// thing here that can reach everybody at once, so it always previews first and
// says exactly how many people that turned out to be.

const AUDIENCES = [
  { value: "all",    label: "Everyone with an email address" },
  { value: "roles",  label: "By role" },
  { value: "groups", label: "By group" },
  { value: "users",  label: "Named people" },
];

export default function AnnouncementsTab({ groups, users }) {
  const [form, setForm] = useState({
    subject: "", body: "", audience: "all",
    roles: [], group_ids: [], user_ids: [], include_inactive: false,
  });
  const [preview, setPreview] = useState(null);
  const [result, setResult]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  const set = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setPreview(null); setResult(null);
  };

  const toggle = (key, value) => {
    setForm(f => {
      const list = f[key];
      return { ...f, [key]: list.includes(value) ? list.filter(v => v !== value) : [...list, value] };
    });
    setPreview(null); setResult(null);
  };

  const run = async (commit) => {
    setBusy(true); setError(null);
    try {
      const url = commit ? "/api/admin/announcements" : "/api/admin/announcements/preview";
      const out = await api.post(url, form);
      if (commit) { setResult(out); setPreview(null); } else { setPreview(out); }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Send an announcement</h3>
      <p style={{ margin: "0 0 18px", color: "#6b7280", fontSize: 13 }}>
        For things people need to hear outside the normal notifications — planned downtime,
        a change of address, a storage migration. It always shows you who it would reach
        before anything is sent, and there is no undo afterwards.
      </p>

      {error && (
        <div role="alert" style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      <Field label="Subject" required>
        <Input value={form.subject} onChange={set("subject")}
               placeholder="Planned downtime on Saturday" />
      </Field>
      <Field label="Message" required>
        <Textarea rows={8} value={form.body} onChange={set("body")}
                  placeholder="The portal will be unavailable from 08:00 to 12:00 on Saturday…" />
      </Field>

      <Field label="Send to">
        <Select options={AUDIENCES} value={form.audience} onChange={set("audience")} />
      </Field>

      {form.audience === "roles" && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "4px 0 14px" }}>
          {ROLE_ORDER.map(r => (
            <label key={r} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.roles.includes(r)}
                     onChange={() => toggle("roles", r)} />
              {ROLE_LABELS[r]}
            </label>
          ))}
        </div>
      )}

      {form.audience === "groups" && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "4px 0 14px" }}>
          {groups.map(g => (
            <label key={g.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.group_ids.includes(g.id)}
                     onChange={() => toggle("group_ids", g.id)} />
              {g.name}
            </label>
          ))}
        </div>
      )}

      {form.audience === "users" && (
        <div style={{ maxHeight: 220, overflowY: "auto", margin: "4px 0 14px", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
          {(users || []).map(u => (
            <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0" }}>
              <input type="checkbox" checked={form.user_ids.includes(u.id)}
                     onChange={() => toggle("user_ids", u.id)} />
              <span>{u.full_name || u.email || "— no address —"}</span>
              {u.full_name && <span style={{ color: "#6b7280" }}>{u.email || "— no address —"}</span>}
            </label>
          ))}
        </div>
      )}

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 16 }}>
        <input type="checkbox" checked={form.include_inactive}
               onChange={e => { setForm(f => ({ ...f, include_inactive: e.target.checked })); setPreview(null); }} />
        Include deactivated accounts
        <span style={{ color: "#6b7280" }}>
          — normally left out: they cannot sign in, and the address may have moved on
        </span>
      </label>

      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="secondary" onClick={() => run(false)} disabled={busy || !form.subject.trim() || !form.body.trim()}>
          {busy && !preview ? <><Spinner /> Checking…</> : "Show who this reaches"}
        </Btn>
        {preview && (
          <Btn onClick={() => run(true)} disabled={busy || preview.count === 0}>
            {busy ? <><Spinner /> Sending…</> : `Send to ${preview.count} ${preview.count === 1 ? "person" : "people"}`}
          </Btn>
        )}
      </div>

      {preview && (
        <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <strong style={{ fontSize: 14 }}>
            This would reach {preview.count} {preview.count === 1 ? "person" : "people"}
          </strong>
          <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", fontSize: 12, color: "#374151" }}>
            {preview.recipients.map(r => (
              <div key={r.id}>{r.email}</div>
            ))}
            {preview.truncated && <div style={{ color: "#6b7280" }}>…and more</div>}
          </div>
        </div>
      )}

      {result && (
        <div role="status" style={{ marginTop: 16, padding: "14px 16px", borderRadius: 8, background: result.failed.length ? "rgba(217,119,6,0.08)" : "rgba(0,150,64,0.08)", border: "1px solid #e5e7eb", fontSize: 13 }}>
          Sent to {result.sent} of {result.total}.
          {result.failed.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {result.failed.map((f, i) => <li key={i}>{f.email} — {f.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
